import React, { useEffect, useMemo } from "react";

import { TagsFilter } from "../TagsFilter";
import { i18n } from "../../translate/i18n";
import {
	BulkContactsTable,
	bulkContactKey,
	IconClear,
	IconSearch,
	IconSpinner,
	formatBulkPhone,
	formatBulkPhoneInputDisplay,
	isValidBulkManualPhone,
	normalizeBulkPhoneDigits,
} from "./bulkMessagingUi";

export default function BulkMessagingContactsStep({
	addContactsType,
	setAddContactsType,
	searchParam,
	setSearchParam,
	setContactsPage,
	selectedTags,
	setSelectedTags,
	getContacts,
	contacts,
	setContacts,
	loadingContacts,
	toTransferContacts,
	setToTransferContacts,
	bulkMessage,
	setBulkMessage,
	campaignContacts,
	displayChannel,
	handleCreateContact,
	newUser,
	setNewUser,
	csvName,
	setContactsByCSV,
	lastCsvImportCount,
	csvImportRejected,
	includedPanelRef,
	selectedContacts,
	setSelectedContacts,
}) {
	const contactColumns = [
		{
			key: "name",
			label: i18n.t("bulkMessaging.wizard.contactsStep.contactName"),
			render: (row) => row.name,
		},
		{
			key: "number",
			label:
				displayChannel === "email"
					? "Email"
					: i18n.t("bulkMessaging.wizard.contactsStep.contactNumber"),
			render: (row) =>
				displayChannel === "email" ? row.number : formatBulkPhone(row.number),
		},
	];

	const campaignNumberKeys = useMemo(() => {
		const keys = new Set();
		(campaignContacts || []).forEach((contact) => {
			const key = bulkContactKey(contact);
			if (key) keys.add(key);
		});
		return keys;
	}, [campaignContacts]);

	const availableContacts = useMemo(
		() =>
			(contacts || []).filter((contact) => {
				const key = bulkContactKey(contact);
				return key && !campaignNumberKeys.has(key);
			}),
		[contacts, campaignNumberKeys]
	);

	useEffect(() => {
		setToTransferContacts((prev) =>
			prev.filter((contact) => !campaignNumberKeys.has(bulkContactKey(contact)))
		);
	}, [campaignNumberKeys, setToTransferContacts]);

	const isInCampaign = (row) => campaignNumberKeys.has(bulkContactKey(row));

	const isTransferSelected = (row) =>
		toTransferContacts.some((item) => bulkContactKey(item) === bulkContactKey(row));

	const toggleTransfer = (row) => {
		if (isInCampaign(row)) return;
		const rowKey = bulkContactKey(row);
		if (isTransferSelected(row)) {
			setToTransferContacts((prev) =>
				prev.filter((item) => bulkContactKey(item) !== rowKey)
			);
		} else {
			setToTransferContacts((prev) => [...prev, row]);
		}
	};

	const applyManualNumber = (raw) => {
		setNewUser((prev) => ({
			...prev,
			number: normalizeBulkPhoneDigits(raw),
		}));
	};

	const manualNumberValid = isValidBulkManualPhone(newUser?.number);

	const addSelectedToCampaign = () => {
		if (!toTransferContacts.length) return;
		setBulkMessage((prev) => {
			const existingKeys = new Set(
				(prev.contacts || []).map((contact) => bulkContactKey(contact)).filter(Boolean)
			);
			const toAdd = toTransferContacts.filter((contact) => {
				const key = bulkContactKey(contact);
				return key && !existingKeys.has(key);
			});
			if (!toAdd.length) return prev;
			return {
				...prev,
				contacts: [...(prev.contacts || []), ...toAdd],
			};
		});
		setToTransferContacts([]);
	};

	return (
		<div className="bulk-messaging-contacts-step w-full space-y-4">
			<p className="m-0 text-sm text-default-600 dark:text-default-400">
				{i18n.t("bulkMessaging.wizard.contactsStep.intro")}
			</p>

			<div
				className="bulk-messaging-contacts-step__tabs"
				role="tablist"
				aria-label={i18n.t("bulkMessaging.modal.steps.contacts")}
			>
				<button
					type="button"
					role="tab"
					aria-selected={addContactsType === "leads"}
					className={[
						"bulk-messaging-contacts-step__tab",
						addContactsType === "leads" && "is-active",
					]
						.filter(Boolean)
						.join(" ")}
					onClick={() => setAddContactsType("leads")}
				>
					{i18n.t("bulkMessaging.wizard.contactsStep.tabRegistered")}
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={addContactsType === "csv"}
					className={[
						"bulk-messaging-contacts-step__tab",
						addContactsType === "csv" && "is-active",
					]
						.filter(Boolean)
						.join(" ")}
					onClick={() => setAddContactsType("csv")}
				>
					{i18n.t("bulkMessaging.wizard.contactsStep.tabImport")}
				</button>
			</div>

			<div className="bulk-messaging-contacts-step__columns">
				<div className="bulk-messaging-contacts-step__panel bulk-messaging-contacts-step__panel--available">
					<div className="bulk-messaging-contacts-step__panel-header">
						<h6 className="card-title m-0">
							{i18n.t("bulkMessaging.wizard.contactsStep.panelAvailable")}
						</h6>
						{addContactsType === "leads" && (
							<span className="bulk-messaging-contacts-step__count bulk-messaging-contacts-step__count--muted">
								{i18n.t("bulkMessaging.wizard.contactsStep.searchedCount", {
									count: availableContacts.length,
								})}
							</span>
						)}
					</div>

					{addContactsType === "leads" && (
						<div className="relative flex min-h-0 flex-1 flex-col gap-3">
							{loadingContacts && (
								<div
									className="bulk-messaging-contacts-step__loading-overlay"
									role="status"
									aria-live="polite"
									aria-busy="true"
								>
									<IconSpinner className="app-modal-spinner !size-8" />
									<p className="m-0 text-sm font-medium text-default-700 dark:text-default-200">
										{i18n.t("bulkMessaging.wizard.contactsStep.loadingContacts")}
									</p>
								</div>
							)}

							<fieldset
								disabled={loadingContacts}
								className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 border-0 p-0 m-0 disabled:opacity-60"
							>
							<div className="grid gap-3 md:grid-cols-[1fr_minmax(0,200px)_auto] md:items-end">
								<div>
									<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
										{i18n.t("bulkMessaging.wizard.contactsStep.searchPlaceholder")}
									</label>
									<div className="app-input-with-icons">
										<input
											type="text"
											inputMode="search"
											autoComplete="off"
											disabled={loadingContacts}
											className={[
												"form-input form-input-sm ps-9",
												searchParam.length > 0 ? "pe-9" : "pe-3",
											].join(" ")}
											value={searchParam}
											onChange={(e) => setSearchParam(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter" && !loadingContacts) {
													e.preventDefault();
													getContacts();
												}
											}}
											placeholder={i18n.t(
												"bulkMessaging.wizard.contactsStep.searchInputPlaceholder"
											)}
										/>
										<span className="app-input-with-icons__start" aria-hidden>
											<IconSearch />
										</span>
										{searchParam.length > 0 && (
											<span className="app-input-with-icons__end">
												<button
													type="button"
													className="app-input-with-icons__clear"
													onClick={() => setSearchParam("")}
													aria-label={i18n.t(
														"bulkMessaging.wizard.contactsStep.clearSearch"
													)}
												>
													<IconClear />
												</button>
											</span>
										)}
									</div>
								</div>
								<div>
									<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
										{i18n.t("bulkMessaging.wizard.contactsStep.searchTags")}
									</label>
									<TagsFilter
										variant="tailwick"
										onFiltered={(tagIds) => {
											setContactsPage(1);
											setSelectedTags(tagIds);
										}}
									/>
								</div>
								<button
									type="button"
									className="btn btn-primary shrink-0"
									disabled={loadingContacts}
									onClick={() => getContacts()}
								>
									{i18n.t("bulkMessaging.wizard.contactsStep.searchButton")}
								</button>
							</div>

							<p className="m-0 text-sm text-default-500">
								{i18n.t("bulkMessaging.wizard.contactsStep.selectHint")}
							</p>

							<BulkContactsTable
								columns={contactColumns}
								rows={availableContacts}
								selected={toTransferContacts}
								isRowSelected={isTransferSelected}
								onToggle={toggleTransfer}
								onToggleAll={(checked) => {
									if (checked) {
										setToTransferContacts((prev) => {
											const keys = new Set(
												prev.map((c) => bulkContactKey(c)).filter(Boolean)
											);
											const merged = [...prev];
											for (const row of availableContacts) {
												const key = bulkContactKey(row);
												if (key && !keys.has(key)) {
													keys.add(key);
													merged.push(row);
												}
											}
											return merged;
										});
									} else {
										setToTransferContacts((prev) => {
											const visibleKeys = new Set(
												availableContacts
													.map((c) => bulkContactKey(c))
													.filter(Boolean)
											);
											return prev.filter(
												(c) => !visibleKeys.has(bulkContactKey(c))
											);
										});
									}
								}}
								emptyMessage={i18n.t("bulkMessaging.wizard.contactsStep.emptyAvailable")}
							/>

							<button
								type="button"
								className="btn btn-primary w-full"
								disabled={loadingContacts || !toTransferContacts.length}
								onClick={addSelectedToCampaign}
							>
								{i18n.t("bulkMessaging.wizard.contactsStep.addSelected", {
									count: toTransferContacts.length,
								})}
							</button>
							</fieldset>
						</div>
					)}

					{addContactsType === "csv" && (
						<div className="space-y-4">
							<p className="m-0 text-sm text-default-600 dark:text-default-400">
								{i18n.t("bulkMessaging.wizard.contactsStep.importHint")}
							</p>
							<a
								href="/example.csv"
								download
								className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
							>
								{i18n.t("bulkMessaging.wizard.contactsStep.downloadSample")}
							</a>
							<div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
								<input
									type="text"
									className="form-input min-w-0 flex-1"
									value={csvName}
									readOnly
									disabled
									placeholder={i18n.t(
										"bulkMessaging.wizard.contactsStep.filePlaceholder"
									)}
								/>
								<label className="btn btn-primary shrink-0 cursor-pointer sm:self-stretch">
									{i18n.t("bulkMessaging.wizard.contactsStep.browse")}
									<input
										type="file"
										accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
										className="sr-only"
										onChange={setContactsByCSV}
									/>
								</label>
							</div>
							{loadingContacts && (
								<div className="app-modal-loading !py-2">
									<IconSpinner />
								</div>
							)}
							{lastCsvImportCount > 0 && (
								<p className="m-0 text-sm font-medium text-primary">
									{i18n.t("bulkMessaging.wizard.contactsStep.importAddedHint", {
										count: lastCsvImportCount,
									})}
								</p>
							)}
							{csvImportRejected?.length > 0 && (
								<div className="rounded-lg border border-danger/30 bg-danger/5 p-3 dark:bg-danger/10">
									<p className="mb-2 mt-0 text-sm font-semibold text-danger">
										{i18n.t("bulkMessaging.wizard.contactsStep.importRejectedTitle", {
											count: csvImportRejected.length,
										})}
									</p>
									<div className="max-h-52 overflow-auto rounded-md border border-danger/20">
										<table className="min-w-full divide-y divide-danger/20 text-sm">
											<thead className="sticky top-0 bg-danger/10 text-xs uppercase text-danger">
												<tr>
													<th className="px-2 py-1.5 text-start">
														{i18n.t("bulkMessaging.wizard.contactsStep.importRejectedLine")}
													</th>
													<th className="px-2 py-1.5 text-start">
														{i18n.t("bulkMessaging.wizard.contactsStep.contactName")}
													</th>
													<th className="px-2 py-1.5 text-start">
														{i18n.t("bulkMessaging.wizard.contactsStep.contactNumber")}
													</th>
													<th className="px-2 py-1.5 text-start">
														{i18n.t("bulkMessaging.wizard.contactsStep.importRejectedReason")}
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-danger/10 bg-card">
												{csvImportRejected.map((row, index) => (
													<tr key={`${row.line}-${row.name}-${index}`}>
														<td className="px-2 py-1.5 text-default-600 dark:text-default-400">
															{row.line ?? "-"}
														</td>
														<td className="px-2 py-1.5">{row.name}</td>
														<td className="px-2 py-1.5 break-all">{row.rawNumber}</td>
														<td className="px-2 py-1.5 text-danger">
															{i18n.t(
																`bulkMessaging.wizard.contactsStep.importRejectReasons.${row.reason}`
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
						</div>
					)}
				</div>

				<div
					ref={includedPanelRef}
					className="bulk-messaging-contacts-step__panel bulk-messaging-contacts-step__panel--included"
				>
					<div className="bulk-messaging-contacts-step__panel-header">
						<h6 className="card-title m-0">
							{i18n.t("bulkMessaging.wizard.contactsStep.panelIncluded")}
						</h6>
						<span className="bulk-messaging-contacts-step__count">
							{campaignContacts.length}
						</span>
					</div>

					<div className="bulk-messaging-contacts-step__manual rounded-lg border border-dashed border-default-300 p-3 dark:border-default-600">
						<p className="mb-3 mt-0 text-sm font-medium text-default-700 dark:text-default-300">
							{i18n.t("bulkMessaging.wizard.contactsStep.manualTitle")}
						</p>
						<div className="bulk-messaging-contacts-step__manual-row">
							<div className="bulk-messaging-contacts-step__manual-field">
								<label
									htmlFor="bulk-manual-name"
									className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
								>
									{i18n.t("bulkMessaging.wizard.contactsStep.contactName")}
								</label>
								<input
									id="bulk-manual-name"
									type="text"
									className="form-input form-input-sm w-full"
									value={newUser?.name || ""}
									onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
									onKeyDown={(e) => {
										if (manualNumberValid && e.key === "Enter") {
											handleCreateContact();
										}
									}}
								/>
								<p className="bulk-messaging-contacts-step__manual-feedback" aria-hidden="true">
									&nbsp;
								</p>
							</div>
							<div className="bulk-messaging-contacts-step__manual-field">
								<label
									htmlFor="bulk-manual-number"
									className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
								>
									{i18n.t("bulkMessaging.wizard.contactsStep.contactNumber")}
								</label>
								<input
									id="bulk-manual-number"
									type="tel"
									autoComplete="tel"
									className="form-input form-input-sm w-full"
									value={formatBulkPhoneInputDisplay(newUser?.number || "")}
									placeholder={i18n.t("bulkMessaging.wizard.contactsStep.numberExampleShort")}
									onChange={(e) => applyManualNumber(e.target.value)}
									onPaste={(e) => {
										e.preventDefault();
										const pasted = e.clipboardData.getData("text");
										if (pasted) applyManualNumber(pasted);
									}}
									onKeyDown={(e) => {
										if (manualNumberValid && e.key === "Enter") {
											handleCreateContact();
										}
									}}
								/>
								<p
									className={[
										"bulk-messaging-contacts-step__manual-feedback",
										newUser?.number &&
											!manualNumberValid &&
											"bulk-messaging-contacts-step__manual-feedback--error",
									]
										.filter(Boolean)
										.join(" ")}
								>
									{newUser?.number && !manualNumberValid
										? i18n.t("bulkMessaging.wizard.contactsStep.numberInvalid")
										: "\u00a0"}
								</p>
							</div>
							<div className="bulk-messaging-contacts-step__manual-action">
								<button
									type="button"
									className="btn btn-primary form-input-sm !h-[1.875rem] shrink-0 px-4"
									disabled={!manualNumberValid}
									onClick={handleCreateContact}
								>
									{i18n.t("bulkMessaging.wizard.contactsStep.addManual")}
								</button>
								<p className="bulk-messaging-contacts-step__manual-feedback" aria-hidden="true">
									&nbsp;
								</p>
							</div>
						</div>
					</div>

					<CampaignContactsPanel
						campaignContacts={campaignContacts}
						contactColumns={contactColumns}
						selectedContacts={selectedContacts}
						setSelectedContacts={setSelectedContacts}
						setBulkMessage={setBulkMessage}
					/>
				</div>
			</div>
		</div>
	);
}

function CampaignContactsPanel({
	campaignContacts,
	contactColumns,
	selectedContacts,
	setSelectedContacts,
	setBulkMessage,
}) {
	const toggle = (row) => {
		if (selectedContacts.includes(row)) {
			setSelectedContacts(selectedContacts.filter((item) => item !== row));
		} else {
			setSelectedContacts([...selectedContacts, row]);
		}
	};

	return (
		<div className="bulk-messaging-contacts-step__table-wrap flex min-h-0 flex-1 flex-col gap-2">
			<BulkContactsTable
				columns={contactColumns}
				rows={campaignContacts}
				selected={selectedContacts}
				onToggle={toggle}
				onToggleAll={(checked) => {
					if (checked) {
						setSelectedContacts(campaignContacts.map((c) => c));
					} else {
						setSelectedContacts([]);
					}
				}}
				onRemove={(row) => {
					setBulkMessage((prev) => ({
						...prev,
						contacts: (prev.contacts || []).filter((item) => item !== row),
					}));
				}}
				emptyMessage={i18n.t("bulkMessaging.wizard.contactsStep.emptyCampaign")}
			/>
			<button
				type="button"
				className="btn btn-sm w-full bg-danger/10 text-danger hover:bg-danger/20"
				disabled={!selectedContacts.length}
				onClick={() => {
					setBulkMessage((prev) => ({
						...prev,
						contacts: (prev.contacts || []).filter(
							(item) => !selectedContacts.includes(item)
						),
					}));
					setSelectedContacts([]);
				}}
			>
				{i18n.t("bulkMessaging.wizard.contactsStep.removeSelected", {
					count: selectedContacts.length,
				})}
			</button>
		</div>
	);
}
